import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import TooltipComponent from '../../TooltipComponent';
import InfoTooltip from './InfoTooltip';
import { IInfoParkingTag, IInfoRecord, IInfoRideTag } from '../interfaces';
import { END_TYPE, START_TYPE } from '../constants';

interface IInfoForRide {
  datacyIndex: number;
  data: {
    rideTags: IInfoRideTag[];
    parkingTags: IInfoParkingTag[];
    rideComment: string | null;
    parkingComment: string | null;
    records: IInfoRecord[];
    recordCount?: number;
    summary: {
      waypoint: string | number;
      event: string | number;
      alarm: string | number;
      tag: string | number;
      comment: string | number;
    };
  };
  isAnonym: boolean;
}

export default function InfoForRide(props: IInfoForRide): JSX.Element {
  const { formatMessage: f } = useIntl();

  const getIconColorClass = (type: string) => {
    let color = '';
    switch (type) {
      case 'waypoint':
        color = 'text-green-dark';
        break;
      case 'alarm':
        color = 'text-primary';
        break;
      case 'event':
        color = 'text-violet-dark';
        break;
      case 'comment':
        color = 'text-blue-dark';
        break;
      case 'tag':
        color = 'text-orange-dark';
        break;
      default:
    }
    return color;
  };

  const getGdprElement = () => {
    return (
      <>
        <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />{' '}
        {f({ id: 'tables.tripsOverview.column.datetime.anonymized' })}
      </>
    );
  };

  const getEmptyEvent = () => {
    return !props.isAnonym ? (
      <>{f({ id: 'tables.tripsOverview.noInformation' })}</>
    ) : (
      getGdprElement()
    );
  };

  const getRecordTitle = () => {
    if (
      props.data.records[1]?.type &&
      props.data.records[1]?.type !== START_TYPE &&
      props.data.records[1]?.type !== END_TYPE
    ) {
      return (
        <div className={`d-flex ${getIconColorClass(props.data.records[1]?.type)}`}>
          <i className={`cmd-icons cmd-icon-${props.data.records[1]?.type} fs-5 pr-1`} />
          <span className="d-inline-block w-100 text-truncate align-middle">
            {`${dayjs(props.data.records[1]?.activatedAt).format('H:mm')} - ${
              props.data.records[1]?.title || ''
            }`}
          </span>
        </div>
      );
    }
    if (props.data.rideComment) {
      return (
        <div className="d-flex text-blue-dark">
          <i className="cmd-icons cmd-icon-commentary fs-5 pr-1" />
          <span className="d-inline-block w-100 text-truncate align-middle">
            {props.data.rideComment}
          </span>
        </div>
      );
    }
    if (props.data.rideTags.length) {
      return (
        <div className="d-flex text-c-orange-dark">
          <i className="cmd-icons cmd-icon-single-folded-content fs-5 pr-1" />
          <span className="d-inline-block w-100 text-truncate align-middle">
            {props.data.rideTags[0].name}
            <span
              className="e-badge-sq"
              style={{
                backgroundColor: props.data.rideTags[0].color,
              }}
            />
          </span>
        </div>
      );
    }
    return getEmptyEvent();
  };

  const getNumber = (tooltipValue: number | string) => {
    if (props.isAnonym) return '';
    return typeof tooltipValue === 'number' ? Number(tooltipValue) : 0;
  };

  return (
    <div className="align-middle" style={{ cursor: 'pointer', minWidth: '200px', flex: 1 }}>
      <div className="w-100">
        <TooltipComponent
          id="ride-info"
          overlay={
            <InfoTooltip
              event={getNumber(props.data.summary.event)}
              alarm={getNumber(props.data.summary.alarm)}
              tag={Number(props.data.summary.tag)}
              comment={Number(props.data.summary.comment)}
              waypoint={getNumber(props.data.summary.waypoint)}
              datacyIndex={props.datacyIndex}
            />
          }
          width="208px"
          firstItems
          positionLeft
          bottom="48px"
        >
          <div className="text-nowrap b-drive-color-info">
            <div data-cy={`row-${props.datacyIndex + 1}-info`}>{getRecordTitle()}</div>
            <div>
              <span
                data-cy={`row-${props.datacyIndex + 1}-info_Waypoint`}
                className="mx-2px text-green-dark"
              >
                {String(props.data.summary.waypoint) === '-' ? (
                  <>&ndash;</>
                ) : (
                  props.data.summary.waypoint
                )}
              </span>
              /
              <span
                data-cy={`row-${props.datacyIndex + 1}-info_Event`}
                className="mx-2px text-violet-dark"
              >
                {String(props.data.summary.event) === '-' ? <>&ndash;</> : props.data.summary.event}
              </span>
              /
              <span
                data-cy={`row-${props.datacyIndex + 1}-info_Alarm`}
                className="mx-2px text-primary"
              >
                {String(props.data.summary.alarm) === '-' ? <>&ndash;</> : props.data.summary.alarm}
              </span>
              /
              <span
                data-cy={`row-${props.datacyIndex + 1}-info_Tag`}
                className="mx-2px text-orange-dark"
              >
                {props.data.summary.tag}
              </span>
              /
              <span
                data-cy={`row-${props.datacyIndex + 1}-info_Comment`}
                className="mx-2px text-blue-dark"
              >
                {props.data.summary.comment}
              </span>
            </div>
          </div>
        </TooltipComponent>
      </div>
    </div>
  );
}
